import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { NextBackButtons } from "components";
import CustomerFormFields from "../CustomerFormFields";
import CarPicker from "../CarPicker";

import { updateAppointment } from "../appointments/appointmentSlice";
import {
  captureBooking,
  customerLookup,
  sendToWindowDataLayer,
  serviceLookup,
} from "../utils";

import {
  inputField,
  emptyInputField,
  cardHeaderClass,
  cardBodyClass,
  buttonPrimary,
} from "css";
import { use } from "react";

const VehicleSelection = ({ appointment }) => {
  const [loading, setLoading] = useState(true);
  const [clear, setClear] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [carErrors, setCarErrors] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [inputClicked, setInputClicked] = useState({});
  const [valueCheck, setValueCheck] = useState(["fname", "lname"]);
  const [customer, setCustomer] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [deferredServices, setDeferredServices] = useState([]);
  const [vehicleReset, setVehicleReset] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState(false);
  const [addVehicle, setAddVehicle] = useState(false);

  const dispatch = useDispatch();

  const formFields = [
    {
      type: "text",
      name: "fname",
      required: true,
      placeholder: "First Name",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
    {
      type: "text",
      name: "lname",
      required: true,
      placeholder: "Last Name",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
    {
      type: "text",
      name: "email",
      required: appointment?.shopData?.requireNewCustomerEmail || false,
      placeholder: "Email Address",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
  ];

  const addressFields = [
    {
      type: "text",
      name: "street",
      required: appointment?.shopData?.requireAddress || false,
      placeholder: "Street",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
    {
      type: "text",
      name: "city",
      required: appointment?.shopData?.requireAddress || false,
      placeholder: "City",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
    {
      type: "text",
      name: "state",
      required: appointment?.shopData?.requireAddress || false,
      placeholder: "State",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
    {
      type: "text",
      name: "zip",
      required: appointment?.shopData?.requireAddress || false,
      placeholder: "Zip Code",
      defaultClass: "inputField",
      emptyClass: "emptyInputField",
    },
  ];

  const addOptionalValues = () => {
    if (appointment?.shopData?.requireAddress) {
      valueCheck.push("street", "city", "state", "zip");
    }
    if (appointment?.shopData?.requireNewCustomerEmail) {
      valueCheck.push("email");
    }
  };

  useEffect(() => {
    let config = {
      domain: appointment?.domain,
      shopId: appointment?.shopData?.shopId,
    };

    captureBooking(appointment)
      .then((res) => {
        dispatch(
          updateAppointment({
            updates: {
              bookingId: appointment.bookingId || res.insertedId,
              bailed: false,
            },
          })
        );
        return res;
      })
      .then(async (res) => {
        const customer = await checkCustomer(
          appointment?.customer?.phone,
          config
        );

        if (customer) {
          setVehicles(customer.vehicles);
          dispatch(
            updateAppointment({
              updates: {
                customerId: customer.integrationId,
                dcId: customer._id,
                customer: {
                  ...appointment.customer,
                  fname: customer.fname,
                  lname: customer.lname,
                  email: customer.email,
                },
              },
            })
          );

          const updatedAppointment = {
            ...appointment,
            bookingId: appointment.bookingId || res.insertedId,
            bailed: false,
            customerId: customer.integrationId,
            dcId: customer._id,
            customer: {
              ...appointment.customer,
              fname: customer.fname,
              lname: customer.lname,
              email: customer.email,
              address: {
                street: customer.address,
                city: customer.city,
                state: customer.state,
                zip: customer.zip,
              },
            },
          };
          captureBooking(updatedAppointment);
          sendToWindowDataLayer(
            "dc-service-phone_number",
            appointment?.customer?.phone
          );
        }
      })
      .catch((error) => {
        console.error("Error during booking process:", error);
      });
  }, []);

  useEffect(() => {
    addOptionalValues();
  }, []);

  const checkCustomer = async (phone, config) => {
    try {
      const cleanedNumber = phone.replace(/^(\+?1)/, "");
      const data = await customerLookup(cleanedNumber, config);
      if (data._id) {
        setExistingCustomer(true);
        setLoading(false);
        setValueCheck([]);
        return data;
      } else {
        setLoading(false);
        setExistingCustomer(false);
        return null;
      }
    } catch (error) {
      console.error("Error posting booking config:", error);
      return null;
    }
  };

  useEffect(() => {
    enableNext(valueCheck);
  }, [valueCheck, carErrors, fieldErrors]);

  useEffect(() => {
    if (selectedVehicle) {
      const config = {
        domain: appointment?.domain,
        shopId: appointment?.shopData?.shopId,
      };

      const customer = {
        _id: appointment.dcId,
        vehicle_id: selectedVehicle?.crossRef[0]?.crossRef,
      };

      if (appointment?.vehicle?.crossRef.length > 0) {
        fetchServices(customer, config);
      } else {
        dispatch(
          updateAppointment({
            updates: {
              deferredServices: [],
            },
          })
        );
      }
    }
  }, [selectedVehicle]);

  const fetchServices = async (customer, config) => {
    await serviceLookup(customer, config).then((data) => {
      if (data?.detail?.lineItems.length > 0) {
        updateDeferredServices(data?.detail?.lineItems);
      } else {
        dispatch(
          updateAppointment({
            updates: {
              deferredServices: [],
            },
          })
        );
        updateDeferredServices([]);
      }
    });
  };

  const updateDeferredServices = (data) => {
    setDeferredServices(data);
  };

  const handleDeferredService = (e) => {
    dispatch(
      updateAppointment({
        updates: {
          deferredServices: e.target.checked
            ? [...(appointment?.deferredServices || []), e.target.id]
            : (appointment?.deferredServices || []).filter(
                (id) => id !== e.target.id
              ),
        },
      })
    );
  };

  const enableNext = (values) => {
    if (
      // Object.keys(fieldErrors).length === 0 &&
      Object.keys(carErrors).length === 0 &&
      values?.length === 0 &&
      appointment?.vehicle?.year !== "" &&
      appointment?.vehicle?.make !== "" &&
      appointment?.vehicle?.model !== ""
    ) {
      setClear(true);
    } else {
      setClear(false);
    }
  };

  const handleFocus = (fieldName) => {
    setInputClicked({ ...inputClicked, [fieldName]: true });
  };

  const handleBlur = (e, isRequired) => {
    let updatedValues = [];

    if (["street", "city", "state", "zip"].includes(e.target.name)) {
      updateCustomerAddress(e);
    } else {
      updateCustomerInfo(e);
    }

    if (valueCheck !== undefined) {
      if (e.target.value !== "") {
        updatedValues = valueCheck?.filter((item) => item !== e.target.name);
        setValueCheck(updatedValues);
      } else if (!valueCheck?.includes(e.target.name) && isRequired) {
        setValueCheck((oldValues) => [...oldValues, e.target.name]);
      }
    }
  };

  const handleAddVehicle = () => {
    setAddVehicle(!addVehicle);
  };

  const updateCustomerInfo = (e) => {
    let fieldName = e.target.name;
    let fieldValue = e.target.value;

    switch (fieldName) {
      case "fname":
        if (fieldValue === "") {
          setFieldErrors({
            ...fieldErrors,
            fname: "First Name is required",
          });
        } else {
          delete fieldErrors.fname;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment?.customer,
                  [fieldName]: fieldValue,
                },
              },
            })
          );
        }
        break;

      case "lname":
        if (fieldValue === "") {
          setFieldErrors({
            ...fieldErrors,
            lname: "Last Name is required",
          });
        } else {
          delete fieldErrors.lname;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment?.customer,
                  [fieldName]: fieldValue,
                },
              },
            })
          );
        }
        break;

      case "email":
        if (
          appointment?.shopData?.requireNewCustomerEmail &&
          fieldValue === ""
        ) {
          setFieldErrors({
            ...fieldErrors,
            email: "Email Address is required",
          });
        } else {
          delete fieldErrors.email;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment?.customer,
                  [fieldName]: fieldValue,
                },
              },
            })
          );
        }
        break;

      default:
        break;
    }

    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [fieldName]: fieldValue,
    }));
  };

  const updateCustomerAddress = (e) => {
    let fieldName = e.target.name;
    let fieldValue = e.target.value;

    switch (fieldName) {
      case "street":
        if (appointment?.shopData?.requireAddress && fieldValue === "") {
          setFieldErrors({
            ...fieldErrors,
            street: "Street is required",
          });
        } else {
          delete fieldErrors.street;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment.customer,
                  address: {
                    ...appointment.customer.address,
                    [fieldName]: fieldValue,
                  },
                },
              },
            })
          );
        }
        break;

      case "city":
        if (appointment?.shopData?.requireAddress && fieldValue === "") {
          setFieldErrors({
            ...fieldErrors,
            city: "City is required",
          });
        } else {
          delete fieldErrors.city;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment.customer,
                  address: {
                    ...appointment.customer.address,
                    [fieldName]: fieldValue,
                  },
                },
              },
            })
          );
        }
        break;

      case "state":
        if (appointment?.shopData?.requireAddress && fieldValue === "") {
          setFieldErrors({
            ...fieldErrors,
            state: "State is required",
          });
        } else {
          delete fieldErrors.state;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment.customer,
                  address: {
                    ...appointment.customer.address,
                    [fieldName]: fieldValue,
                  },
                },
              },
            })
          );
        }
        break;

      case "zip":
        if (appointment?.shopData?.requireAddress && fieldValue === "") {
          setFieldErrors({
            ...fieldErrors,
            zip: "Zip Code is required",
          });
        } else {
          delete fieldErrors.zip;
          dispatch(
            updateAppointment({
              updates: {
                customer: {
                  ...appointment.customer,
                  address: {
                    ...appointment.customer.address,
                    [fieldName]: fieldValue,
                  },
                },
              },
            })
          );
        }
        break;

      default:
        break;
    }

    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [fieldName]: fieldValue,
    }));
  };

  return (
    <>
      {loading && (
        <div className="flex justify-center items-center py-12">
          <center>
            <img
              src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
              alt="loading"
            />
            <br />
            <br />
            Fetching your information...
          </center>
        </div>
      )}

      {!loading && existingCustomer && (
        <div>
          <div className="text-2xl font-semibold pb-2 mx-6 pt-6">
            Welcome Back, {appointment?.customer?.fname}!
          </div>

          <div className="grid gap-2 md:grid-cols-2 mx-6">
            {vehicles?.map((vehicle, index) => (
              <div
                key={index}
                onClick={() => {
                  setSelectedVehicle(vehicle);
                  setVehicleReset(false);
                  dispatch(
                    updateAppointment({
                      updates: { vehicle: vehicle },
                    })
                  );

                  if (appointment?.shopData?.oldestYearServicable) {
                    if (
                      vehicle?.year >=
                      appointment?.shopData?.oldestYearServicable
                    ) {
                      setClear(true);
                    } else {
                      setClear(false);
                    }
                  } else {
                    setClear(true);
                  }

                  document.getElementById("buttons").scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }}
              >
                <div className="w-full">
                  <div
                    className={`${cardHeaderClass} flex justify-between`}
                    style={{
                      backgroundColor:
                        selectedVehicle?.crossRef?.[0]?.crossRef ===
                        vehicle?.crossRef?.[0]?.crossRef
                          ? "green"
                          : appointment?.shopData?.color,
                    }}
                  >
                    {vehicle.year} {vehicle.make} {vehicle.model}
                    {appointment?.vehicle?.id === vehicle.id && (
                      <>
                        <span className="relative flex h-3 w-3 pt-1.5">
                          <span className="relative inline-flex rounded-full h-3 w-3"></span>
                        </span>
                      </>
                    )}
                  </div>
                  <div
                    className={`${cardBodyClass} cursor-pointer`}
                    style={{
                      borderColor:
                        appointment?.vehicle?.crossRef?.[0]?.crossRef ===
                        vehicle?.crossRef?.[0]?.crossRef
                          ? "green"
                          : appointment?.shopData?.color,
                    }}
                  >
                    <center>
                      <img
                        src="https://bookings.d14e.io/images/service/car-silhouette.png"
                        alt="generic car"
                        className="opacity-50 max-w-full h-auto"
                      />
                    </center>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {deferredServices?.length > 0 && !appointment?.vehicleId && (
            <div
              className="bg-[#daecf7] border-solid border-[1px] border-[#bfe0f5] text-[#297190] mt-5 px-4 py-3 mx-6 mb-5 rounded relative"
              role="alert"
            >
              <strong className="font-bold">Heads Up!</strong>
              <span className="block sm:inline ml-1">
                The following services were previously declined. Would you like
                to add them?
              </span>
              <div className="mt-2">
                {deferredServices.map((service, index) => (
                  <div key={index} className="flex items-center mb-4">
                    <label className="flex items-center cursor-pointer relative">
                      <input
                        type="checkbox"
                        className="peer h-5 w-5 cursor-pointer transition-all appearance-none rounded shadow hover:shadow-md border bg-[#fdfeff] border-slate-600 checked:bg-[#256885] checked:border-[#256885]"
                        id={service.description}
                        onClick={(e) => {
                          handleDeferredService(e);
                        }}
                      />
                      <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3.5 w-3.5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          stroke="currentColor"
                          strokeWidth="1"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                      </span>
                    </label>
                    <span className="ml-2">{service.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="w-full">
            {vehicles?.length === 0 ? (
              <>
                <CarPicker
                  vehicleId={vehicles?.length}
                  appointment={appointment}
                  selectedVehicle={selectedVehicle}
                  setSelectedVehicle={setSelectedVehicle}
                  vehicleReset={vehicleReset}
                  setVehicleReset={setVehicleReset}
                  deferredServices={deferredServices}
                  setDeferredServices={setDeferredServices}
                  setCarErrors={setCarErrors}
                />
              </>
            ) : (
              <>
                <center>
                  <div className="flex items-center my-4 px-4">
                    <div className="flex-grow h-px bg-gray-400"></div>
                    <span className="px-4 text-gray-600 font-medium">OR</span>
                    <div className="flex-grow h-px bg-gray-400"></div>
                  </div>

                  <div
                    className={`p-4 ${buttonPrimary}`}
                    style={{
                      backgroundColor: "white",
                      cursor: "pointer",
                      boxShadow:
                        appointment?.shopData?.shadow === "shadow-md"
                          ? `0 4px 6px ${appointment?.shopData?.color}33`
                          : "none",
                      border: `1px solid ${appointment?.shopData?.color}`,
                      fontColor: "black",
                      width: "30%",
                    }}
                    onClick={() => handleAddVehicle()}
                  >
                    Add New Vehicle
                  </div>
                  {addVehicle && (
                    <CarPicker
                      vehicleId={vehicles?.length}
                      appointment={appointment}
                      selectedVehicle={selectedVehicle}
                      setSelectedVehicle={setSelectedVehicle}
                      vehicleReset={vehicleReset}
                      setVehicleReset={setVehicleReset}
                      deferredServices={deferredServices}
                      setDeferredServices={setDeferredServices}
                      setCarErrors={setCarErrors}
                    />
                  )}
                </center>
              </>
            )}
          </div>
        </div>
      )}

      {!loading && !existingCustomer && (
        <>
          <div className="pt-5 mb-3 text-3xl font-bold">
            <center>Looks like you're a new customer! 👋</center>
          </div>

          {Object.keys(fieldErrors).length > 0 && (
            <div className="mx-6 mb-5 p-1 bg-red-200 border-solid border-[1px] border-red-500 rounded-md text-red-500">
              <ul>
                {Object.keys(fieldErrors).map((error, index) => (
                  <li key={index}>{fieldErrors[error]}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-grow flex-col my-5 gap-2">
            <CustomerFormFields
              props={{
                formFields: formFields,
                appointment: appointment,
                inputClicked: inputClicked,
                valueCheck: valueCheck,
                emptyInputField: emptyInputField,
                inputField: inputField,
                updateCustomerInfo: updateCustomerInfo,
                handleFocus: handleFocus,
                handleBlur: handleBlur,
              }}
            />
          </div>

          {appointment?.shopData?.requireAddress && (
            <div className="flex flex-grow flex-col mt-10 mb-5 gap-2">
              <CustomerFormFields
                props={{
                  formFields: addressFields,
                  appointment: appointment,
                  inputClicked: inputClicked,
                  valueCheck: valueCheck,
                  emptyInputField: emptyInputField,
                  inputField: inputField,
                  updateCustomerAddress: updateCustomerAddress,
                  handleFocus: handleFocus,
                  handleBlur: handleBlur,
                }}
              />
            </div>
          )}

          <div className="text-xl mt-5 mx-6">
            Please enter your vehicle information below. If you don't see your
            make, model, or year simply type it in.
          </div>

          <div id="buttons" className="w-full">
            <CarPicker
              vehicleId={vehicles?.length}
              appointment={appointment}
              selectedVehicle={selectedVehicle}
              setSelectedVehicle={setSelectedVehicle}
              vehicleReset={vehicleReset}
              setVehicleReset={setVehicleReset}
              deferredServices={deferredServices}
              setDeferredServices={setDeferredServices}
              setCarErrors={setCarErrors}
            />
          </div>
        </>
      )}

      <br />
      <NextBackButtons appointment={appointment} clear={clear} confirm="true" />
    </>
  );
};

export { VehicleSelection };
